/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ErrorData } from '@types';
import dayjs from 'dayjs';

type Loading =
  | 'fetch-unique-contacts-report-summary'
  | 'fetch-unique-contacts-report-price-by-range'
  | 'fetch-unique-contacts-report-contact-stats'
  | 'fetch-unique-contacts-full-report';

// eslint-disable-next-line no-shadow
export enum CallConsumptionReportGranularity {
  DAILY = 'DAILY',
  MONTHLY = 'MONTHLY',
}

// eslint-disable-next-line no-shadow
export enum CallConsumptionReportGranularityType {
  consumption = 'consumption',
  calls = 'calls',
  minutes = 'minutes',
}
export type CallConsumptionReportGranularities = {
  consumption: CallConsumptionReportGranularity;
  calls: CallConsumptionReportGranularity;
  minutes: CallConsumptionReportGranularity;
};

export type CallConsumptionChartWithFilters =
  | CallConsumptionReportGranularityType.calls
  | CallConsumptionReportGranularityType.minutes;

export type SelectedDate = [Date | null, Date | null];

export type CallConsumptionReportSummary = {
  currency: string;
  timezone: string;
  incoming: { minutes: number; cost: number; count: number };
  outgoing: { minutes: number; cost: number; count: number };
  total: { minutes: number; cost: number; count: number };
};

export type CallConsumptionReportCountSeries = {
  timezone: string;
  points: {
    date: string;
    incoming: { count: number };
    outgoing: { count: number };
  }[];
};

export type CallConsumptionReportCostSeries = {
  currency: string;
  timezone: string;
  points: { date: string; cost: number }[];
};

export type CountSeriesBarsVisibility = {
  incoming: boolean;
  outgoing: boolean;
};

export type CallConsumptionReportState = {
  error: ErrorData | null;
  loading: Loading[];
  filters: {
    selectedDate: SelectedDate;
    granularity: {
      consumption: CallConsumptionReportGranularity;
      calls: CallConsumptionReportGranularity;
      minutes: CallConsumptionReportGranularity;
    };
    chartFilters: {
      calls: {
        showOutgoingBar: boolean;
        showIncomingBar: boolean;
      };
      minutes: {
        showOutgoingBar: boolean;
        showIncomingBar: boolean;
      };
    };
  };
  summary: CallConsumptionReportSummary | null;
  countSeries: CallConsumptionReportCountSeries | null;
  costSeries: CallConsumptionReportCostSeries | null;
  countSeriesBarsVisibility: CountSeriesBarsVisibility;
};

type CallConsumptionReportReducer<P = void> = (
  state: CallConsumptionReportState,
  action: PayloadAction<P>
) => void;

const initialState: CallConsumptionReportState = {
  error: null,
  loading: [],
  filters: {
    selectedDate: [
      dayjs().startOf('month').toDate(),
      dayjs().endOf('month').toDate(),
    ],

    granularity: {
      consumption: CallConsumptionReportGranularity.DAILY,
      calls: CallConsumptionReportGranularity.DAILY,
      minutes: CallConsumptionReportGranularity.DAILY,
    },
    chartFilters: {
      calls: {
        showOutgoingBar: true,
        showIncomingBar: true,
      },
      minutes: {
        showOutgoingBar: true,
        showIncomingBar: true,
      },
    },
  },
  summary: null,
  countSeries: null,
  costSeries: null,
  countSeriesBarsVisibility: {
    incoming: true,
    outgoing: true,
  },
};

export type HandleFetchUniqueContactsSummaryReport = {
  startDate: string;
  endDate: string;
};

const handleSetCallConsumptionSelectedDate: CallConsumptionReportReducer<
  SelectedDate
> = (state, action) => {
  state.filters.selectedDate = action.payload;
};

const handleSetConsumptionReportGranularity: CallConsumptionReportReducer<{
  type: CallConsumptionReportGranularityType;
  value: CallConsumptionReportGranularity;
}> = (state, action) => {
  const { type, value } = action.payload;
  state.filters.granularity[type] = value;
};

const handleSetCallConsumptionChartFilters: CallConsumptionReportReducer<{
  type: CallConsumptionChartWithFilters;
  value: {
    showOutgoingBar: boolean;
    showIncomingBar: boolean;
  };
}> = (state, action) => {
  const { type, value } = action.payload;
  state.filters.chartFilters[type] = value;
};

const handleFetchCallConsumptionMetricsSummary: CallConsumptionReportReducer =
  () => {};

const handleFetchCallConsumptionMetricsSummarySuccess: CallConsumptionReportReducer<
  CallConsumptionReportSummary
> = (state, action) => {
  state.summary = action.payload;
};
const handleFetchCallConsumptionMetricsSummaryFailure: CallConsumptionReportReducer<
  ErrorData | null
> = (state, action) => {
  state.error = action.payload;
};

const handleFetchCallConsumptionMetricsCountSeries: CallConsumptionReportReducer =
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  () => {};

const handleFetchCallConsumptionMetricsCountSeriesSuccess: CallConsumptionReportReducer<
  CallConsumptionReportCountSeries
> = (state, action) => {
  state.countSeries = action.payload;
};
const handleFetchCallConsumptionMetricsCountSeriesFailure: CallConsumptionReportReducer<
  ErrorData | null
> = (state, action) => {
  state.error = action.payload;
};

const handleFetchCallConsumptionMetricsCostSeries: CallConsumptionReportReducer =
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  () => {};

const handleFetchCallConsumptionMetricsCostSeriesSuccess: CallConsumptionReportReducer<
  CallConsumptionReportCostSeries
> = (state, action) => {
  state.costSeries = action.payload;
};
const handleFetchCallConsumptionMetricsCostSeriesFailure: CallConsumptionReportReducer<
  ErrorData | null
> = (state, action) => {
  state.error = action.payload;
};
const handleSetCountSeriesBarsVisibility: CallConsumptionReportReducer<
  CountSeriesBarsVisibility
> = (state, action) => {
  state.countSeriesBarsVisibility = action.payload;
};

const CallConsumptionReportSlice = createSlice({
  name: 'CallConsumptionReportSlice',
  initialState,
  reducers: {
    setCallConsumptionSelectedDate: handleSetCallConsumptionSelectedDate,
    setConsumptionReportGranularity: handleSetConsumptionReportGranularity,
    setCallConsumptionChartFilters: handleSetCallConsumptionChartFilters,
    fetchCallConsumptionMetricsSummary:
      handleFetchCallConsumptionMetricsSummary,
    fetchCallConsumptionMetricsSummarySuccess:
      handleFetchCallConsumptionMetricsSummarySuccess,
    fetchCallConsumptionMetricsSummaryFailure:
      handleFetchCallConsumptionMetricsSummaryFailure,
    fetchCallConsumptionMetricsCountSeries:
      handleFetchCallConsumptionMetricsCountSeries,
    fetchCallConsumptionMetricsCountSeriesSuccess:
      handleFetchCallConsumptionMetricsCountSeriesSuccess,
    fetchCallConsumptionMetricsCountSeriesFailure:
      handleFetchCallConsumptionMetricsCountSeriesFailure,
    fetchCallConsumptionMetricsCostSeries:
      handleFetchCallConsumptionMetricsCostSeries,
    fetchCallConsumptionMetricsCostSeriesSuccess:
      handleFetchCallConsumptionMetricsCostSeriesSuccess,
    fetchCallConsumptionMetricsCostSeriesFailure:
      handleFetchCallConsumptionMetricsCostSeriesFailure,
    setCountSeriesBarsVisibility: handleSetCountSeriesBarsVisibility,
  },
});

const { actions, reducer } = CallConsumptionReportSlice;

export const {
  fetchCallConsumptionMetricsSummary,
  fetchCallConsumptionMetricsSummarySuccess,
  fetchCallConsumptionMetricsSummaryFailure,
  fetchCallConsumptionMetricsCountSeries,
  fetchCallConsumptionMetricsCountSeriesSuccess,
  fetchCallConsumptionMetricsCountSeriesFailure,
  fetchCallConsumptionMetricsCostSeries,
  fetchCallConsumptionMetricsCostSeriesSuccess,
  fetchCallConsumptionMetricsCostSeriesFailure,
  setCallConsumptionSelectedDate,
  setConsumptionReportGranularity,
  setCallConsumptionChartFilters,
  setCountSeriesBarsVisibility,
} = actions;

export default reducer;
